import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { useTenantScope } from "../../hooks/useTenantScope";
import type {
  AdminRulesCategory,
  AdminRulesCategoryPanelProps,
  AdminRulesTableProps,
  AnyAdminRulesTabConfig,
} from "./types";
import { FictionalRulesTabContent } from "./components/FictionalRulesTabContent";
import { FeaturedBadgeRulesTabContent } from "./components/FeaturedBadgeRulesTabContent";
import { EventAdvancedConfigRulesTabContent } from "./components/EventAdvancedConfigRulesTabContent";
import { RulesTabComingSoon } from "./components/RulesTabComingSoon";
import { defaultTabRegistry, getVisibleTabs } from "./tabRegistry";

const RULES_CATEGORY_STORAGE_KEY = "grups.bo.rulesCategory";

const readStoredRulesCategory = (
  tabs: ReadonlyArray<AnyAdminRulesTabConfig>
): AdminRulesCategory | undefined => {
  const stored = sessionStorage.getItem(RULES_CATEGORY_STORAGE_KEY);
  if (!stored) {
    return undefined;
  }

  return getVisibleTabs(tabs).find((tab) => tab.category === stored)?.category;
};

const CATEGORY_PANEL_BY_KEY: Partial<
  Record<AdminRulesCategory, React.ComponentType<AdminRulesCategoryPanelProps>>
> = {
  fictional: FictionalRulesTabContent,
  "featured-badge": FeaturedBadgeRulesTabContent,
  "event-advanced-config": EventAdvancedConfigRulesTabContent,
};

export const AdminRulesTable = ({
  tabs = defaultTabRegistry,
  defaultTabId,
  className,
}: AdminRulesTableProps) => {
  const visibleTabs = useMemo(() => getVisibleTabs(tabs), [tabs]);

  const defaultCategory = useMemo(() => {
    const targetId = defaultTabId ?? visibleTabs.find((tab) => tab.enabled)?.category;
    const match = visibleTabs.find((tab) => tab.category === targetId);
    return match?.category ?? visibleTabs[0]?.category;
  }, [defaultTabId, visibleTabs]);

  const [selectedCategory, setSelectedCategory] = useState<AdminRulesCategory | undefined>(
    () => readStoredRulesCategory(tabs) ?? defaultCategory
  );
  const [createTrigger, setCreateTrigger] = useState(0);
  const [isPanelFormOpen, setIsPanelFormOpen] = useState(false);
  const tenantScope = useTenantScope();
  const needsTenantSelection = !tenantScope.loading && !tenantScope.locked;

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }

    sessionStorage.setItem(RULES_CATEGORY_STORAGE_KEY, selectedCategory);
  }, [selectedCategory]);

  const selectedTab = useMemo(
    () => visibleTabs.find((tab) => tab.category === selectedCategory),
    [selectedCategory, visibleTabs]
  );

  const handleCategoryChange = ({ value }: DropdownChangeEvent) => {
    const nextCategory = value as AdminRulesCategory | undefined;
    setSelectedCategory(nextCategory);
    setIsPanelFormOpen(false);
  };

  const handleAddRule = () => {
    if (!selectedTab?.enabled || !selectedCategory || !tenantScope.locked) {
      return;
    }

    setCreateTrigger((current) => current + 1);
  };

  if (visibleTabs.length === 0 || !selectedTab || !selectedCategory) {
    return null;
  }

  const CategoryPanel = CATEGORY_PANEL_BY_KEY[selectedCategory];

  return (
    <div className={className}>
      {!isPanelFormOpen && (
        <div className="mb-4">
          <div className="flex flex-wrap align-items-end gap-3">
            <FloatLabel className="w-full md:w-20rem">
              <Dropdown
                id="adminRulesCategory"
                value={selectedCategory}
                onChange={handleCategoryChange}
                options={visibleTabs}
                optionLabel="label"
                optionValue="category"
                className="w-full"
              />
              <label htmlFor="adminRulesCategory">Tipo de regla</label>
            </FloatLabel>
            <Button
              label="Agregar regla"
              onClick={handleAddRule}
              disabled={!selectedTab.enabled || !tenantScope.locked}
            />
          </div>
          {needsTenantSelection && (
            <small className="block text-red-500 mt-2">
              Seleccioná un tenant para operar
            </small>
          )}
        </div>
      )}

      {!selectedTab.enabled && selectedTab.comingSoon ? (
        <RulesTabComingSoon label={selectedTab.label} />
      ) : CategoryPanel ? (
        <CategoryPanel
          createTrigger={createTrigger}
          onFormVisibilityChange={setIsPanelFormOpen}
        />
      ) : (
        <RulesTabComingSoon label={selectedTab.label} />
      )}
    </div>
  );
};
